import Selector from '@/components/inputs/selector/selector.component'
import Loader from '@/components/loader/loader.component'
import TipComponent from '@/components/tip/tip.component'
import { localForageHelper, onUserChange, User } from '@/hub-shared'
import { ModalStatusTypes } from '@/interfaces/socialnetworks.interface'
import defaultImage from '@assets/img/jpg/image.jpg'
import Button from '@components/buttons/button/button.component'
import Input from '@components/inputs/input/input.component'
import useDebounce from '@hooks/use-debounce/use-debounce.hook'
import HubCoreService from '@services/hub-core/hub-core.service'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import { Code } from '../../../modal-vinculate-social-network/components/tutorial-instagram/hooks/use-new-tutorial-instagram/use-new-tutorial-instagram.hook'
import Collapse from '@components/accordion-collapse/collapse/collapse.component'
import ModalPublicProfile from '../modal-public-profile/modal-public-profile'
import ModalInstagramProfile from '../modal-instagram-profile/modal-instagram-profile'
import { FacebookDiscoveryUser } from '@/interfaces/user.interface'

import './username-to-connect.component.scoped.scss'
import './username-to-connect.component.scss'

interface Props {
  onOpenTutorial: (stepCode: Code) => void
  onBusinessProfileSelected: (businessProfile: FacebookDiscoveryUser) => void
  businessProfileSelected?: FacebookDiscoveryUser
  onLoadProfile: (isLoading: boolean) => void
  setModalStatus: (status: ModalStatusTypes) => void
  handleOpenTutorial?: (stepCode: Code) => void
  height: number
}

export interface ProfileFound {
  id: string
  username: string
  profile_picture: string
  full_name: string
}

export default ({ onOpenTutorial, onBusinessProfileSelected, businessProfileSelected, onLoadProfile, height }: Props) => {
  const hubCoreService = useMemo(() => new HubCoreService(), [])
  const formRef = useRef<HTMLFormElement>(null)

  const [username, setUsername] = useState('')
  const [generalLoading, setGeneralLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [profileFound, setProfileFound] = useState<FacebookDiscoveryUser | null>(null)
  const [profileNotFound, setProfileNotFound] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [openModalPublicProfile, setOpenModalPublicProfile] = useState(false)
  const [openModalInstagramProfile, setOpenModalInstagramProfile] = useState(false)

  const [t] = useTranslation('modalVinculateSocialNetwork')

  const handleLoadingProfile = useCallback(
    (isLoading: boolean) => {
      if (onLoadProfile && typeof onLoadProfile === 'function') {
        onLoadProfile(isLoading)
      }
    },
    [onLoadProfile]
  )

  const setFocusOnInputElement = useCallback(() => {
    if (formRef.current) {
      const inputElement = formRef.current.querySelector('input')
      inputElement?.focus
    }
  }, [])

  const getProfileInBusinessDiscovery = useDebounce(async (username: string) => {
    try {
      setLoading(true)

      const profile = await hubCoreService.getProfiles(username)

      if (!profile) {
        setProfileFound(null)
        setProfileNotFound(true)
      } else {
        setProfileFound(profile)
        setProfileNotFound(false)
      }
    } catch (error) {
      setProfileFound(null)
      setProfileNotFound(true)
    } finally {
      setLoading(false)
      setFocusOnInputElement()
    }
  })

  const handleChangeUsername = useCallback(
    (username: string) => {
      if (onBusinessProfileSelected && typeof onBusinessProfileSelected === 'function') {
        onBusinessProfileSelected(null)
      }
      setUsername(username.trim())
      getProfileInBusinessDiscovery(username.toLowerCase())
    },
    [getProfileInBusinessDiscovery, onBusinessProfileSelected]
  )

  const handleSelectProfile = (profile: FacebookDiscoveryUser) => {
    if (onBusinessProfileSelected && typeof onBusinessProfileSelected === 'function') {
      onBusinessProfileSelected(profile)
    }
  }

  const handleOpenTutorial = useCallback(
    (stepCode: Code) => {
      if (onOpenTutorial && typeof onOpenTutorial === 'function') {
        onOpenTutorial(stepCode)
      }
    },
    [onOpenTutorial]
  )

  useEffect(() => {
    handleLoadingProfile(loading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffect(() => {
    if (user) {
      const getUsernameByIdProfile = async (idProfile: string) => {
        return user.identities.find((identity) => identity.id === idProfile)?.username
      }

      const initFlow = async () => {
        const { action, idProfile } =
          ((await localForageHelper.getItem('actions')) as { action: 'addAccount' | 'renew'; idProfile?: string }) || {}
        if (action === 'renew' && idProfile) {
          const username = await getUsernameByIdProfile(idProfile)
          const profileFound = await hubCoreService.getProfiles(username)
          if (profileFound) {
            handleSelectProfile(profileFound)
            await localForageHelper.setItem('businessProfileSelected', profileFound)
            await localForageHelper.setItem('idProfileRenewed', idProfile)
            handleOpenTutorial('HAS_FACEBOOK_PAGE')
          } else {
            handleOpenTutorial('HOW_CHANGE_BUSINESS_ACCOUNT')
          }
        } else {
          setGeneralLoading(false)
        }
      }

      initFlow()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    const onUserChangeSubscription: SubscriptionLike = onUserChange.subscribe((userChanged) => {
      setUser(userChanged)
      setLoading(false)
    })

    return () => onUserChangeSubscription?.unsubscribe()
  }, [])

  if (generalLoading) {
    return (
      <div className="display-flex justify-content-center py-5" style={{ width: '100%' }}>
        <Loader size="big" />
      </div>
    )
  }

  return (
    <>
      <div
        className="display-flex justify-content-center"
        style={{ height: window?.innerWidth < 991 ? `calc(${height}px - 131px)` : 'auto' }}
      >
        <form className="username-to-connect p-3" ref={formRef} onSubmit={(e) => e?.preventDefault()}>
          <div className="mb-7 mt-2">
            <h1>{t('findUsername')}</h1>
            {businessProfileSelected?.id ? (
              <div className="redirect-message display-flex align-items-center mt-2" style={{ gap: '0.5rem' }}>
                <i className="fa-regular fa-circle-exclamation" style={{ minWidth: 'fit-content' }}></i>
                <p>{t('facebookRedirect')}</p>
              </div>
            ) : null}
          </div>
          <Input
            autoFocus
            name="usernameToConnect"
            id="usernameToConnect"
            leftLabel={'@'}
            placeholder="username"
            value={username}
            loading={loading}
            onChange={handleChangeUsername}
            onTimeout={setLoading}
          />
          <div className={`profiles-found scrollbar mb-3 ${loading && 'p-2'}`}>
            {!loading && profileFound && (
              <div
                className="profile-found-item"
                role="button"
                tabIndex={0}
                key={profileFound.id}
                onClick={() => handleSelectProfile(profileFound)}
              >
                <div className="profile-image-found">
                  <figure>
                    <img
                      src={profileFound?.profile_picture_url}
                      alt={profileFound?.username}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = defaultImage
                      }}
                    />
                  </figure>
                </div>
                <div className="profile-description-found">
                  <span className="profile-fullname">{profileFound?.name}</span>
                  <span className="profile-username">{`@${profileFound?.username}`}</span>
                </div>
                <Selector
                  name="select-profile"
                  id="select-profile"
                  errorSpan={false}
                  className="selector-profile"
                  value={profileFound}
                  checked={businessProfileSelected?.id === profileFound?.id}
                  type="radio"
                  onChange={() => handleSelectProfile(profileFound)}
                />
              </div>
            )}
            {!loading && profileNotFound && (
              <div className="list-empty">
                <p className="text-center" dangerouslySetInnerHTML={{ __html: t('notFoundUsername') }} />
              </div>
            )}
          </div>
          {!loading && profileNotFound && (
            <>
              <Collapse
                bodyBorder={true}
                headerBorder="1px solid var(--border_color)"
                title={t('profileNotFound')}
                colorIcons="var(--text_color)"
                headerJustify="flex-start"
              >
                <p>{t('profileNotFoundVerify')}</p>
                <div className="link" role="button" tabIndex={0} onClick={() => setOpenModalPublicProfile(true)}>
                  <i className="fa-regular fa-circle-question" />
                  <p>{t('publicProfile')}?</p>
                </div>
                <div className="link" role="button" tabIndex={0} onClick={() => setOpenModalInstagramProfile(true)}>
                  <i className="fa-regular fa-circle-question" />
                  <p>{t('instagramProfile')}?</p>
                </div>
              </Collapse>
              <Collapse
                bodyBorder={true}
                headerBorder="1px solid var(--border_color)"
                title={t('talkToUs')}
                colorIcons="var(--text_color)"
                headerJustify="flex-start"
              >
                <p className="mb-3">{t('talkToUsComplement')}</p>
                <Button
                  onClick={() =>
                    window.open(
                      'https://api.whatsapp.com/send?phone=5511948972387&text=Ola,%20Wake%20Creators!%20Preciso%20de%20suporte,%20pode%20me%20ajudar',
                      '_blank'
                    )
                  }
                  width="100%"
                  inverted
                  borderColor="var(--primary_color)"
                  color="transparent"
                  textColor="var(--primary_color)"
                >
                  <i className="fa-solid fa-comment-question mr-2"></i>
                  {t('talk')}
                </Button>
              </Collapse>
            </>
          )}
        </form>
      </div>
      <ModalPublicProfile openModal={openModalPublicProfile} onCloseModal={() => setOpenModalPublicProfile(false)} />
      <ModalInstagramProfile openModal={openModalInstagramProfile} onCloseModal={() => setOpenModalInstagramProfile(false)} />
    </>
  )
}
